// What? A React component for the Basic Info tab in document type creation/editing.
// Why? To provide a clean interface for users to define the fundamental properties of a document type (name and description).
// How? The component's logic flow:
// 1. Initialize: Load existing document type data if editing
// 2. Form Management: Handle name and description field changes
// 3. Validation: Validate required fields with helpful error messages
// 4. State Management: Sync local changes with parent component
// 5. Loading State: Show loading indicator while fetching data

import React, { useEffect } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Info as InfoIcon } from "@mui/icons-material";

import Loading from "../../../../../components/Loading";
import { useWorkspaceContext } from "../../../../../providers/WorkspaceProvider";
import { useFetchDocumentType } from "../../../hooks/useDocumentTypes";
import { useValidation } from "../../../contexts/ValidationContext";

interface BasicInfoTabProps {
  name: string;
  description: string;
  customInstructions: string;
  documentTypeId?: number;
  onChange: (updates: {
    name: string;
    description: string;
    customInstructions: string;
  }) => void;
  onInitialLoad?: (updates: {
    name: string;
    description: string;
    customInstructions: string;
  }) => void;
  showValidation: boolean;
}

const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  name,
  description,
  customInstructions,
  documentTypeId,
  onChange,
  onInitialLoad,
  showValidation,
}) => {
  const { workspace } = useWorkspaceContext();
  const { documentType, loading } = useFetchDocumentType(
    documentTypeId || 0,
    workspace?.id || "",
    undefined,
    "Draft",
  );
  const { setShowValidation } = useValidation();

  // Load data when editing existing document type
  useEffect(() => {
    if (documentTypeId && documentType && !loading) {
      const newName = documentType.Name;
      const newDescription = documentType.Description || "";
      const newCustomInstructions =
        (documentType as any).CustomInstructions || "";

      // Only update if the data is different and it's the initial load
      if (
        name === "" &&
        description === "" &&
        customInstructions === "" &&
        (newName !== name ||
          newDescription !== description ||
          newCustomInstructions !== customInstructions)
      ) {
        const payload = {
          name: newName,
          description: newDescription,
          customInstructions: newCustomInstructions,
        };
        // Use onInitialLoad when available so the parent can populate the
        // context without treating this as a user-made change.
        if (onInitialLoad) {
          onInitialLoad(payload);
        } else {
          onChange(payload);
        }
      }
    }
  }, [
    documentTypeId,
    documentType,
    loading,
    name,
    description,
    customInstructions,
    onChange,
  ]);

  // Handler for name field changes
  const handleNameChange = (newName: string) => {
    setShowValidation(false);
    onChange({ name: newName, description, customInstructions });
  };

  // Handler for description field changes
  const handleDescriptionChange = (newDescription: string) => {
    onChange({ name, description: newDescription, customInstructions });
  };

  // Handler for custom instructions field changes
  const handleCustomInstructionsChange = (newCustomInstructions: string) => {
    onChange({ name, description, customInstructions: newCustomInstructions });
  };

  // Validation logic for Name field
  const getNameError = (): string | undefined => {
    if (!showValidation) return undefined;

    const trimmed = name?.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length < 3) return "Name must be at least 3 characters";
    if (trimmed.length > 100) return "Name must be less than 100 characters";

    return undefined;
  };

  const nameError = getNameError();

  // Character count color logic for Custom Instructions
  const getCharacterCountColor = (): string => {
    const length = customInstructions.length;
    if (length >= 2000) return "danger"; // Red
    if (length >= 1800) return "warning"; // Yellow/orange
    return "neutral"; // Default
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Stack spacing={3}>
      {/* Name Field */}
      <FormControl required error={!!nameError}>
        <FormLabel htmlFor="name">Name</FormLabel>
        <Input
          id="name"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Enter document type name"
          slotProps={{ input: { maxLength: 100 } }}
          size="lg"
        />
        <FormHelperText>
          {nameError || "Give your document type a clear, descriptive name"}
        </FormHelperText>
      </FormControl>

      {/* Description Field */}
      <FormControl>
        <FormLabel htmlFor="description">Description</FormLabel>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Enter a detailed description of this document type (optional)"
          minRows={4}
          slotProps={{ textarea: { maxLength: 500 } }}
          size="lg"
        />
        <FormHelperText>
          Provide additional context about this document type's purpose and
          usage
        </FormHelperText>
      </FormControl>

      {/* Custom Instructions Field */}
      <FormControl>
        <FormLabel htmlFor="customInstructions">Custom Instructions</FormLabel>
        <Box sx={{ position: "relative" }}>
          <Textarea
            id="customInstructions"
            value={customInstructions}
            onChange={(e) => handleCustomInstructionsChange(e.target.value)}
            placeholder="Enter custom instructions for section generation (optional)"
            minRows={4}
            slotProps={{ textarea: { maxLength: 2000 } }}
            size="lg"
          />
          <Typography
            level="body-xs"
            sx={{
              position: "absolute",
              top: 8,
              right: 12,
              color:
                getCharacterCountColor() === "danger"
                  ? "danger.500"
                  : getCharacterCountColor() === "warning"
                    ? "warning.500"
                    : "text.tertiary",
              pointerEvents: "none",
              backgroundColor: "background.surface",
              px: 0.5,
              borderRadius: "sm",
            }}
          >
            {customInstructions.length}/2000
          </Typography>
        </Box>
        <FormHelperText>
          Provide additional context and instructions that should be followed
          when generating sections
          <Tooltip
            placement="top"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: "bold" }}>
                  Examples of custom instructions:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Typography component="li" level="body-sm" sx={{ mb: 0.5 }}>
                    "Never mention names of people in the source material"
                  </Typography>
                  <Typography component="li" level="body-sm" sx={{ mb: 0.5 }}>
                    "Keep a strict professional tone"
                  </Typography>
                  <Typography component="li" level="body-sm">
                    "The text should be user-friendly and easily read by anyone
                    that doesn't have deep technical knowledge"
                  </Typography>
                </Box>
              </Box>
            }
          >
            <InfoIcon
              sx={{
                ml: 1,
                fontSize: "1rem",
                verticalAlign: "middle",
                cursor: "help",
              }}
            />
          </Tooltip>
        </FormHelperText>
      </FormControl>
    </Stack>
  );
};

export default BasicInfoTab;
